<template>
  <div ref="wrapperElement" class="project-wayline-wrapper height-100">
    <a-spin :spinning="loading" :delay="300" tip="下载中" size="large">
    <div ref="headerElement">
    <div style="height: 50px; line-height: 50px; border-bottom: 1px solid #4f4f4f; font-weight: 450;">
      <a-row>
        <a-col :span="1"></a-col>
        <a-col :span="15">航线库</a-col>
        <a-col :span="8" v-if="importVisible" class="flex-row flex-justify-end flex-align-center">
          <a-tooltip v-if="!editorDraft" title="创建航线（支持基于 DJI Dock 3 创建）">
            <a-button type="text" style="color: white;" @click="createVisible = true">
              <PlusOutlined />
            </a-button>
          </a-tooltip>
          <a-upload
            v-if="!editorDraft"
            name="file"
            :multiple="false"
            :before-upload="beforeUpload"
            :show-upload-list="false"
            :customRequest="uploadFile"
          >
            <a-button type="text" style="color: white;">
              <SelectOutlined />
            </a-button>
          </a-upload>
        </a-col>
      </a-row>
    </div>
    <a-alert v-if="importVisible && previewHeightWarning" class="wayline-height-warning" type="warning" show-icon :message="previewHeightWarning" />
    </div>
    <div ref="scrollElement" :style="{ height : height + 'px'}" class="scrollbar">
      <WaylineEditor
        v-if="editorDraft"
        :key="editorKey"
        :draft="editorDraft"
        :source-wayline-id="editorSourceId"
        @close="closeEditor"
        @saved="onEditorSaved"
      />
      <div id="data" class="height-100 uranus-scrollbar" v-else-if="waylinesData.data.length !== 0" @scroll="onScroll">
        <div v-for="wayline in waylinesData.data" :key="wayline.id">
          <div
            class="wayline-panel"
            :class="{ selected: selectedWaylineId === wayline.id, previewing: previewingWaylineId === wayline.id }"
            style="padding-top: 5px;"
            @click="selectRoute(wayline)"
          >
            <div class="title">
              <a-tooltip :title="wayline.name">
                <div class="pr10" style="width: 120px; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;">{{ wayline.name }}</div>
              </a-tooltip>
              <div class="ml10"><UserOutlined /></div>
              <a-tooltip :title="wayline.user_name">
                <div class="ml5 pr10" style="width: 80px; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;">{{ wayline.user_name }}</div>
              </a-tooltip>
              <div class="fz20" @click.stop>
                <a-dropdown @click.stop>
                  <a style="color: white;">
                    <EllipsisOutlined />
                  </a>
                  <template #overlay>
                    <a-menu theme="dark" class="more" style="background: #3c3c3c;">
                      <a-menu-item v-if="importVisible" @click.stop="editWayline(wayline)">
                        <span>编辑</span>
                      </a-menu-item>
                      <a-menu-item @click.stop="downloadWayline(wayline.id, wayline.name)">
                        <span>下载</span>
                      </a-menu-item>
                      <a-menu-item @click.stop="showWaylineTip(wayline.id)">
                        <span>删除</span>
                      </a-menu-item>
                    </a-menu>
                  </template>
                </a-dropdown>
              </div>
            </div>
            <div class="ml10 mt5" style="color: hsla(0,0%,100%,0.65);">
              <span><RocketOutlined /></span>
              <span class="ml5">{{ DEVICE_NAME[wayline.drone_model_key] }}</span>
              <span class="ml10"><CameraFilled style="border-top: 1px solid; padding-top: -3px;" /></span>
              <span class="ml5" v-for="payload in wayline.payload_model_keys" :key="payload.id">
                {{ DEVICE_NAME[payload] }}
              </span>
              <span class="ml10 type-tag">{{ templateTypeText(wayline.template_types) }}</span>
            </div>
            <div class="mt5 ml10" style="color: hsla(0,0%,100%,0.35);">
              <span class="mr10">Update at {{ new Date(wayline.update_time).toLocaleString() }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <a-empty :image-style="{ height: '60px', marginTop: '60px' }" />
      </div>
      <CreateWaylineModal v-model:visible="createVisible" :author="username" @create="openEditor" />
      <a-modal v-model:visible="deleteTip" width="450px" :closable="false" :maskClosable="false" centered :okButtonProps="{ danger: true }" @ok="deleteWayline">
          <p class="pt10 pl20" style="height: 50px;">航线文件删除后无法恢复，是否继续？</p>
          <template #title>
              <div class="flex-row flex-justify-center">
                  <span>删除</span>
              </div>
          </template>
      </a-modal>
    </div>
    </a-spin>
  </div>
</template>

<script lang="ts" setup>
import { reactive } from '@vue/reactivity'
import { message } from 'ant-design-vue'
import { getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { deleteWaylineFile, downloadWaylineFile, getWaylineFiles, importKmzFile } from '/@/api/wayline'
import { ELocalStorageKey, ERouterName } from '/@/types'
import { EllipsisOutlined, RocketOutlined, CameraFilled, UserOutlined, SelectOutlined, PlusOutlined } from '@ant-design/icons-vue'
import { DEVICE_NAME } from '/@/types/device'
import { useMyStore } from '/@/store'
import { WaylineFile } from '/@/types/wayline'
import { downloadFile } from '/@/utils/common'
import { IPage } from '/@/api/http/type'
import { getRoot } from '/@/root'
import { ParsedWaylinePreview, parseKmzWaylinePreview, readKmzWaylineDocuments } from '/@/utils/wayline-kmz'
import { parseWaylineDraftDocuments } from '/@/utils/wayline-kmz-writer'
import { loadGeoidModel } from '/@/utils/geoid'
import type { WaylineDraft } from '/@/types/wayline-editor'
import CreateWaylineModal from '/@/components/wayline/CreateWaylineModal.vue'
import WaylineEditor from '/@/components/wayline/WaylineEditor.vue'
import { GeojsonCoordinate } from '/@/types/map'
import { wgs84togcj02 } from '/@/vendors/coordtransform.js'
import { useGMapCover } from '/@/hooks/use-g-map-cover'
import { cesiumMapState, clearCesiumWayline, setCesiumWayline } from '/@/store/cesium-map'
import axios from 'axios'
import type { CancelTokenSource } from 'axios'

const loading = ref(false)
const store = useMyStore()
const WAYLINE_PREVIEW_COVER_ID = '__wayline_preview__'
const WAYLINE_PREVIEW_COLOR = '#19be6b'
// A departing page must never clear a newer page's preview in the shared host.
const cesiumWaylineOwner = `wayline-preview:${getCurrentInstance()!.uid}`
let previewRequestId = 0
let previewDownloadSource: CancelTokenSource | null = null
let rawParsedPreview: ParsedWaylinePreview | null = null
let rawPreviewName = ''
let resizeObserver: ResizeObserver | null = null
let loadMoreTimer: ReturnType<typeof setInterval> | null = null
const wayline2DCoverIds = new Set<string>()
const pagination :IPage = {
  page: 1,
  total: -1,
  page_size: 10
}

const waylinesData = reactive({
  data: [] as WaylineFile[]
})

const root = getRoot()
const workspaceId = localStorage.getItem(ELocalStorageKey.WorkspaceId)!
const deleteTip = ref(false)
const deleteWaylineId = ref<string>('')
const canRefresh = ref(true)
const importVisible = ref<boolean>(root.$router.currentRoute.value.name === ERouterName.WAYLINE)
const height = ref()
const selectedWaylineId = ref<string>('')
const previewingWaylineId = ref<string>('')
const previewHeightWarning = ref('')
const wrapperElement = ref<HTMLDivElement | null>(null)
const headerElement = ref<HTMLDivElement | null>(null)
const scrollElement = ref<HTMLDivElement | null>(null)
const username = localStorage.getItem(ELocalStorageKey.Username) || ''
const createVisible = ref(false)
const editorDraft = ref<WaylineDraft | null>(null)
const editorSourceId = ref<string | undefined>(undefined)
const editorKey = ref(0)
const TEMPLATE_TYPE_TEXT: Record<number, string> = { 0: '航点航线', 1: '面状航线', 2: '倾斜摄影', 3: '带状航线' }

function templateTypeText (types?: number[]): string {
  const known = (types || []).map(type => TEMPLATE_TYPE_TEXT[type]).filter(Boolean)
  return known.length ? known.join('/') : '航线'
}

function openEditor (draft: WaylineDraft, sourceId?: string) {
  previewRequestId++
  rawParsedPreview = null
  previewHeightWarning.value = ''
  cancelPendingPreviewDownload()
  clearRenderedWaylinePreview()
  selectedWaylineId.value = ''
  editorSourceId.value = sourceId
  editorDraft.value = draft
  editorKey.value++
}

function closeEditor () {
  editorDraft.value = null
  editorSourceId.value = undefined
}

function onEditorSaved () {
  closeEditor()
  pagination.total = 0
  pagination.page = 1
  waylinesData.data = []
  getWaylines()
}

async function editWayline (wayline: WaylineFile) {
  loading.value = true
  try {
    const kmzFile = await downloadWaylineFile(workspaceId, wayline.id)
    if (!kmzFile) return
    const documents = await readKmzWaylineDocuments(kmzFile)
    // EGM96 templates need the geoid grid to read back as ellipsoid heights;
    // the parser warns by itself when it is unavailable.
    await loadGeoidModel().catch(() => {})
    const result = parseWaylineDraftDocuments(documents, wayline.name, username)
    result.warnings.forEach(warning => message.warning(warning))
    openEditor(result.draft, wayline.id)
  } catch (error) {
    message.error((error as Error).message || '航线文件无法编辑。')
  } finally {
    loading.value = false
  }
}

function updateListHeight () {
  if (isStandaloneWaylineRoute() && wrapperElement.value && scrollElement.value) {
    const bottom = Math.min(document.body.clientHeight, wrapperElement.value.getBoundingClientRect().bottom)
    height.value = Math.max(100, bottom - scrollElement.value.getBoundingClientRect().top)
  }
}

onMounted(() => {
  // The route library plans on terrain and imagery only; no mapping model is loaded here.
  if (isStandaloneWaylineRoute()) cesiumMapState.modelsEnabled = false
  const parent = document.getElementsByClassName('scrollbar').item(0)?.parentNode as HTMLDivElement
  height.value = document.body.clientHeight - parent.firstElementChild!.clientHeight
  if (isStandaloneWaylineRoute()) {
    nextTick(updateListHeight)
    window.addEventListener('resize', updateListHeight)
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateListHeight)
      if (headerElement.value) resizeObserver.observe(headerElement.value)
      if (wrapperElement.value) resizeObserver.observe(wrapperElement.value)
    }
  }
  getWaylines()

  loadMoreTimer = setInterval(() => {
    const data = document.getElementById('data')?.lastElementChild as HTMLDivElement
    if (pagination.total === 0 || Math.ceil(pagination.total / pagination.page_size) <= pagination.page || height.value <= data?.clientHeight + data?.offsetTop) {
      if (loadMoreTimer !== null) clearInterval(loadMoreTimer)
      loadMoreTimer = null
      return
    }
    pagination.page++
    getWaylines()
  }, 1000)
})

onBeforeUnmount(() => {
  cesiumMapState.modelsEnabled = true
  editorDraft.value = null
  previewRequestId++
  rawParsedPreview = null
  if (loadMoreTimer !== null) clearInterval(loadMoreTimer)
  resizeObserver?.disconnect()
  window.removeEventListener('resize', updateListHeight)
  cancelPendingPreviewDownload()
  clearRenderedWaylinePreview()
})

function getWaylines () {
  if (!canRefresh.value) {
    return
  }
  canRefresh.value = false
  getWaylineFiles(workspaceId, {
    page: pagination.page,
    page_size: pagination.page_size,
    order_by: 'update_time desc'
  }).then(res => {
    if (res.code !== 0) {
      return
    }
    waylinesData.data = [...waylinesData.data, ...res.data.list]
    pagination.total = res.data.pagination.total
    pagination.page = res.data.pagination.page
  }).finally(() => {
    canRefresh.value = true
  })
}

function showWaylineTip (waylineId: string) {
  deleteWaylineId.value = waylineId
  deleteTip.value = true
}

function deleteWayline () {
  deleteWaylineFile(workspaceId, deleteWaylineId.value).then(res => {
    if (res.code === 0) {
      message.success('航线文件已删除')
      if (selectedWaylineId.value === deleteWaylineId.value) {
        previewRequestId++
        rawParsedPreview = null
        previewHeightWarning.value = ''
        cancelPendingPreviewDownload()
        clearRenderedWaylinePreview()
        selectedWaylineId.value = ''
      }
    }
    deleteWaylineId.value = ''
    deleteTip.value = false
    pagination.total = 0
    pagination.page = 1
    waylinesData.data = []
    getWaylines()
  })
}

function downloadWayline (waylineId: string, fileName: string) {
  loading.value = true
  downloadWaylineFile(workspaceId, waylineId).then(res => {
    if (!res) {
      return
    }
    const data = new Blob([res], { type: 'application/zip' })
    downloadFile(data, fileName + '.kmz')
  }).finally(() => {
    loading.value = false
  })
}

function selectRoute (wayline: WaylineFile) {
  store.commit('SET_SELECT_WAYLINE_INFO', wayline)
  selectedWaylineId.value = wayline.id
  previewWaylineOnMap(wayline)
}

async function previewWaylineOnMap (wayline: WaylineFile) {
  cancelPendingPreviewDownload()
  const requestId = ++previewRequestId
  rawParsedPreview = null
  previewHeightWarning.value = ''
  const downloadSource = axios.CancelToken.source()
  previewDownloadSource = downloadSource
  previewingWaylineId.value = wayline.id
  clearRenderedWaylinePreview()

  try {
    const kmzFile = await downloadWaylineFile(workspaceId, wayline.id, downloadSource.token)
    if (requestId !== previewRequestId) {
      return
    }
    if (!kmzFile) {
      message.warning('航线文件暂不可预览。')
      return
    }

    const preview = await parseKmzWaylinePreview(kmzFile)
    if (requestId !== previewRequestId) {
      return
    }
    const pointCount = preview.routes.reduce((total, route) => total + route.points.length, 0)
    if (pointCount === 0) {
      message.warning('未解析到航点坐标。')
      clearRenderedWaylinePreview()
      return
    }
    if (!isStandaloneWaylineRoute() && (!root.$map || !root.$aMap)) {
      message.warning('地图尚未加载完成，请稍后再试。')
      return
    }

    rawParsedPreview = preview
    rawPreviewName = wayline.name
    await renderCachedWaylinePreview(true)
    if (requestId !== previewRequestId) return
    if (pointCount === 1) {
      message.warning('该航线仅解析到一个航点。')
    }
    if (!isStandaloneWaylineRoute() && preview.missingHeight) {
      message.warning('部分航点未提供有效高度，已显示二维预览。')
    }
  } catch (error) {
    if (requestId === previewRequestId && !axios.isCancel(error)) {
      console.error('Preview wayline failed:', error)
      message.error('航线预览失败，请检查航线文件。')
      clearRenderedWaylinePreview()
    }
  } finally {
    if (requestId === previewRequestId) {
      if (previewDownloadSource === downloadSource) {
        previewDownloadSource = null
      }
      previewingWaylineId.value = ''
    }
  }
}

function cancelPendingPreviewDownload () {
  previewDownloadSource?.cancel('Wayline preview superseded.')
  previewDownloadSource = null
}

function isStandaloneWaylineRoute (): boolean {
  return root.$router.currentRoute.value.name === ERouterName.WAYLINE
}

async function renderCachedWaylinePreview (fit: boolean) {
  const preview = rawParsedPreview
  if (!preview) return
  if (isStandaloneWaylineRoute()) {
    // Cesium owns its readiness, entities and camera. Publish the unconverted
    // WGS84 source even before the host is ready; no GCJ02 or display-height zero.
    previewHeightWarning.value = setCesiumWayline(preview, rawPreviewName, cesiumWaylineOwner, fit)
    return
  }
  if (!root.$map || !root.$aMap) return
  previewHeightWarning.value = ''
  const mapPreview = convertPreviewToGcj02(preview)
  clearRenderedWaylinePreview()
  renderWayline2D(mapPreview, rawPreviewName, fit)
}

function renderWayline2D (preview: ParsedWaylinePreview, waylineName: string, fit = true) {
  const coverHook = useGMapCover()
  preview.routes.forEach((route, routeIndex) => {
    if (route.points.length === 0) {
      return
    }
    const coverId = `${WAYLINE_PREVIEW_COVER_ID}:${routeIndex}`
    const coordinates = route.points.map(point => [
      point.coordinate[0],
      point.coordinate[1],
      point.height,
    ] as GeojsonCoordinate)
    wayline2DCoverIds.add(coverId)
    if (coordinates.length === 1) {
      coverHook.updatePinElement(coverId, waylineName, coordinates[0], WAYLINE_PREVIEW_COLOR)
    } else {
      coverHook.updatePolylineElement(coverId, waylineName, coordinates, WAYLINE_PREVIEW_COLOR)
    }
  })

  const covers = Array.from(wayline2DCoverIds)
    .flatMap(id => coverHook.getElementFromMap(id) || [])
  if (fit && covers.length > 0) {
    root.$map.setFitView(covers)
  }
}

function clearRenderedWaylinePreview () {
  clearCesiumWayline(cesiumWaylineOwner)
  if (wayline2DCoverIds.size > 0 && root.$map && root.$aMap) {
    const coverHook = useGMapCover()
    coverHook.removeCoverFromMap(WAYLINE_PREVIEW_COVER_ID)
    wayline2DCoverIds.forEach(id => coverHook.removeCoverFromMap(id))
    wayline2DCoverIds.clear()
  }
}

function convertPreviewToGcj02 (preview: ParsedWaylinePreview): ParsedWaylinePreview {
  return {
    ...preview,
    routes: preview.routes.map(route => ({
      ...route,
      points: route.points.map(point => ({
        ...point,
        coordinate: convertCoordinateToGcj02(point.coordinate),
      })),
    })),
    takeoff: preview.takeoff
      ? {
          ...preview.takeoff,
          coordinate: convertCoordinateToGcj02(preview.takeoff.coordinate),
        }
      : null,
  }
}

function convertCoordinateToGcj02 (coordinate: GeojsonCoordinate): GeojsonCoordinate {
  const gcj02 = wgs84togcj02(coordinate[0], coordinate[1])
  return coordinate[2] === undefined ? [gcj02[0], gcj02[1]] : [gcj02[0], gcj02[1], coordinate[2]]
}

function onScroll (e: any) {
  const element = e.srcElement
  if (element.scrollTop + element.clientHeight >= element.scrollHeight - 5 && Math.ceil(pagination.total / pagination.page_size) > pagination.page && canRefresh.value) {
    pagination.page++
    getWaylines()
  }
}

interface FileItem {
  uid: string;
  name?: string;
  status?: string;
  response?: string;
  url?: string;
}

interface FileInfo {
  file: FileItem;
  fileList: FileItem[];
}
const fileList = ref<FileItem[]>([])

function beforeUpload (file: FileItem) {
  fileList.value = [file]
  loading.value = true
  return true
}
const uploadFile = async () => {
  fileList.value.forEach(async (file: FileItem) => {
    const fileData = new FormData()
    fileData.append('file', file, file.name)
    await importKmzFile(workspaceId, fileData).then((res) => {
      if (res.code === 0) {
        message.success(`${file.name} 文件上传成功`)
        canRefresh.value = true
        pagination.total = 0
        pagination.page = 1
        waylinesData.data = []
        getWaylines()
      }
    }).finally(() => {
      loading.value = false
      fileList.value = []
    })
  })
}

</script>

<style lang="scss" scoped>
.wayline-height-warning {
  margin: 8px;
  font-size: 12px;
}
.wayline-panel {
  background: #3c3c3c;
  margin-left: auto;
  margin-right: auto;
  margin-top: 10px;
  height: 90px;
  width: 95%;
  font-size: 13px;
  border-radius: 2px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, opacity 0.2s ease;
  &.selected {
    border-color: #19be6b;
    background: #33423a;
  }
  &.previewing {
    opacity: 0.78;
  }
  .title {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 30px;
    font-weight: bold;
    margin: 0px 10px 0 10px;
  }
}
.type-tag { color: hsla(0,0%,100%,0.45); }
.uranus-scrollbar {
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: #c5c8cc transparent;
}
</style>
